import { Icon, type IconName } from '@smartech/ui';
import React, { useEffect, useRef } from 'react';
import { useScrollLock } from '../../hooks/useScrollLock';

interface ModalProps {
  title?: string;
  subtitle?: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  closeOnBlur?: boolean;
  icon?: IconName;
  mode?: 'error' | 'warning' | 'success' | 'info';
  className?: string;
  containerClassName?: string;
  childrenClassName?: string;
  showWaveBackground?: boolean;
}

const CustomModal: React.FC<ModalProps> = ({
  title,
  subtitle,
  isOpen,
  onClose,
  children,
  icon,
  mode = 'info',
  closeOnBlur = true,
  className = 'w-full',
  containerClassName = '',
  childrenClassName = '',
  showWaveBackground = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Use the robust scroll lock hook
  useScrollLock(isOpen);

  const modeStyle = {
    error: 'text-error-600',
    warning: 'text-warning-600',
    success: 'text-success-600',
    info: 'text-fg-brand-primary',
  }[mode];

  const iconStyle = {
    error: 'bg-bg-error-secondary',
    warning: 'bg-bg-warning-secondary',
    success: 'bg-bg-success-secondary',
    info: 'bg-brand-secondary',
  }[mode];

  useEffect(() => {
    if (!isOpen || !closeOnBlur) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside modal
      if (modalRef.current && modalRef.current.contains(target)) {
        return;
      }

      // Clicks on content rendered outside the modal DOM (portals) must not count as "outside"
      const element = target as Element;
      const bottomSheet = element.closest?.('.bottom-sheet');
      const bottomSheetBackdrop = element.closest?.('.bottom-sheet-backdrop');
      // @smartech/ui Dropdown with usePortal renders the menu under .dropdown-panel-portal on body
      const dropdownPortalPanel = element.closest?.('.dropdown-panel-portal');

      if (bottomSheet || bottomSheetBackdrop || dropdownPortalPanel) {
        return;
      }

      // Click is outside both modal and bottom sheets, so close
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeOnBlur, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[110] bg-bg-overlay opacity-70" />

      <div
        className={`fixed inset-0 z-[111] flex items-center justify-center p-6xl lg:p-0 ${containerClassName}`}
      >
        <div
          ref={modalRef}
          className={`relative max-w-lg rounded-xl bg-white bg-no-repeat p-7xl shadow-lg ${className}`}
          style={
            showWaveBackground
              ? {
                  backgroundImage: `url(/src/assets/img/modal-bg-wave.svg)`,
                  backgroundPosition: 'right top',
                  backgroundSize: '205px auto',
                }
              : undefined
          }
        >
          {(icon || title || subtitle) && (
            <div className="overflow-hidden pb-5xl text-lg">
              {icon && (
                <div
                  className={`relative z-10 mb-3xl flex w-fit items-center gap-md rounded-full ${iconStyle} p-2xl`}
                >
                  <Icon name={icon} className={`text-2xl ${modeStyle}`} />
                </div>
              )}
              <div className="relative z-10 ml-auto">
                {title && (
                  <h2 className="mt-md text-lg font-semibold text-text-primary-900">{title}</h2>
                )}
                {subtitle && <p className="mt-sm text-sm text-text-tertiary-600">{subtitle}</p>}
              </div>
            </div>
          )}
          <button
            onClick={onClose}
            className="absolute left-8xl top-7xl z-10 text-gray-500 hover:text-gray-700"
          >
            <Icon name="x-close" className="text-2xl text-fg-quaternary" />
          </button>

          <div className={`z-10 h-full max-h-[70vh] overflow-y-auto ${childrenClassName}`}>
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomModal;
